// src/lib/trafficGovernor.ts
// ─────────────────────────────────────────────────────────────────────────────
// GOBERNADOR DE TRÁFICO DE NUBE (NIIZ)
// ─────────────────────────────────────────────────────────────────────────────
// Módulo PURO y testeable (sin React, sin Dexie, sin UI). Decide CUÁNDO y CÓMO
// se puede hablar con Supabase, protegiendo SIEMPRE la operación local:
//
//   NORMAL        → latencia sana (< 2.5s) y sin errores 429/503 recientes.
//   CONGESTIONADO → latencia alta (> 2.5s) o algún 429/503 reciente.
//   SOBRECARGA    → señales repetidas de congestión (el servidor está saturado).
//
// REGLAS POR NIVEL DE USUARIO
//   · FREE (sin Naranja Plus): NO hace subida continua de fondo. Todo su
//     historial queda encolado en Dexie y se sube SOLO en el Cierre de Caja o
//     cuando el servidor está libre (ventana de reposo).
//   · PLUS (Naranja Plus): carril prioritario. Nunca se le bloquea la subida;
//     si hay SOBRECARGA escalona sus envíos (Round-Robin) con colas espaciadas
//     en vez de martillar la API en bucle.
//
// El POS, las ventas y la caja NUNCA se detienen por culpa de este módulo:
// aquí solo se decide el ritmo de la nube, jamás se bloquea lo local.
// ─────────────────────────────────────────────────────────────────────────────

import { classifySyncError, type SyncErrorKind } from '@/lib/syncController';

// ─────────────────────────────────────────────────────────────────────────────
// TIPOS PÚBLICOS
// ─────────────────────────────────────────────────────────────────────────────

/** Estado del tráfico hacia la nube. */
export type TrafficState = 'NORMAL' | 'CONGESTIONADO' | 'SOBRECARGA';

/** Nivel del negocio: gratuito (sin Naranja Plus) o de pago (Naranja Plus). */
export type TrafficTier = 'FREE' | 'PLUS';

/** Qué disparó este intento de sincronización. */
export type SyncTrigger =
  | 'background'   // Subida automática en segundo plano
  | 'event'        // Disparado por un evento de UI
  | 'manual'       // Solicitud explícita del usuario
  | 'cash-close'   // Disparado automáticamente al cerrar caja
  | 'idle';        // Ventana de reposo detectada

/** Qué acción tomar con el intento. */
export type TrafficAction = 'GO' | 'STAGGER' | 'DEFER';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTES DE CONFIGURACIÓN
// ─────────────────────────────────────────────────────────────────────────────

/** Latencia (ms) a partir de la cual el servidor se considera lento. */
export const HIGH_LATENCY_MS = 2_500;

/** Ventana (ms) en la que se cuentan las señales de congestión. */
export const CONGESTION_WINDOW_MS = 60_000;

/** Señales acumuladas en la ventana para declarar SOBRECARGA. */
export const SOBRECARGA_SIGNALS = 3;

/** Códigos HTTP que indican saturación del servidor. */
export const OVERLOAD_STATUS_CODES = [429, 503] as const;

/** Espaciado (ms) entre envíos del carril prioritario en SOBRECARGA (Round-Robin). */
export const PLUS_STAGGER_MS = 3_500;

/** Espaciado suave del carril prioritario en CONGESTIONADO. */
export const PLUS_CONGESTION_STAGGER_MS = 1_200;

/** Reposo mínimo (ms) sin actividad para considerar el "servidor libre". */
export const IDLE_SERVER_QUIET_MS = 45_000;

/** Máximo de muestras de latencia que se conservan en memoria. */
const MAX_SAMPLES = 8;

// ─────────────────────────────────────────────────────────────────────────────
// INTERFACES DE ESTADO Y DECISIÓN
// ─────────────────────────────────────────────────────────────────────────────

export interface TrafficSnapshot {
  state: TrafficState;
  /** Última latencia medida (ms). */
  latencyMs: number | null;
  /** Latencia promedio de las últimas muestras (ms). */
  averageLatencyMs: number | null;
  /** Señales de congestión dentro de la ventana. */
  signalsInWindow: number;
  /** ¿El servidor está libre (sin señales y con reposo suficiente)? */
  isIdle: boolean;
  /** ms desde la última actividad de red. */
  quietMs: number;
  /** Mensaje corto en español para la UI. */
  message: string;
}

export interface TrafficDecision {
  action: TrafficAction;
  state: TrafficState;
  tier: TrafficTier;
  trigger: SyncTrigger;
  /** Espera antes de empezar (STAGGER) o motivo del aplazamiento (DEFER). */
  delayMs: number;
  reason: string;
}

export interface EvaluateOptions {
  tier: TrafficTier;
  trigger: SyncTrigger;
  /** Si no hay internet se aplaza siempre (se encola). */
  isOnline?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS INTERNOS
// ─────────────────────────────────────────────────────────────────────────────

/** Timestamp actual en milisegundos. */
const now = (): number => Date.now();

/**
 * Verifica si un error indica saturación del servidor (429 / 503 / rate limit).
 */
/** ¿El código HTTP indica saturación? (sin `as any`: estrecha a 429 | 503). */
const isOverloadStatus = (status: number): boolean =>
  OVERLOAD_STATUS_CODES.some((code) => code === status);

/**
 * Verifica si un error indica saturación del servidor (429 / 503 / rate limit).
 */
export const isOverloadError = (err: unknown): boolean => {
  const anyErr: Record<string, unknown> = err && typeof err === 'object' ? (err as Record<string, unknown>) : {};
  const status = Number(anyErr['status'] ?? anyErr['statusCode'] ?? anyErr['code']);
  
  if (Number.isFinite(status) && isOverloadStatus(status)) return true;
  
  const msg = String(
    anyErr['message'] ||
    anyErr['error_description'] ||
    anyErr['details'] ||
    err || ''
  ).toLowerCase();
  
  return (
    msg.includes('429') ||
    msg.includes('503') ||
    msg.includes('too many requests') ||
    msg.includes('rate limit') ||
    msg.includes('service unavailable') ||
    msg.includes('over capacity')
  );
};

/** Etiqueta legible del estado para UI. */
export const trafficStateLabel = (state: TrafficState): string =>
  state === 'NORMAL' ? 'Normal' : state === 'CONGESTIONADO' ? 'Congestionado' : 'Sobrecarga';

// ─────────────────────────────────────────────────────────────────────────────
// CLASE PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Controlador de tráfico. Una instancia por proceso (singleton).
 * Acumula señales (latencia alta / 429 / 503) dentro de una ventana deslizante
 * y decide la política de subida según el nivel del usuario.
 */
export class TrafficGovernor {
  /** Muestras de latencia recientes. */
  private samples: number[] = [];
  /** Timestamps de cada señal de congestión (peso 1 = latencia, 2 = 429/503). */
  private signals: number[] = [];
  /** Bandera de sobrecarga activa. */
  private overload = false;
  /** Timestamp de la última actividad de red. */
  private lastActivityAt = 0;
  /** Listeners suscritos para actualizaciones de UI. */
  private listeners = new Set<(snapshot: TrafficSnapshot) => void>();

  // ── MEDICIÓN ──

  /** Registra la latencia de una llamada a la nube que salió bien. */
  recordLatency(latencyMs: number): void {
    const value = Number(latencyMs);
    if (!Number.isFinite(value) || value < 0) return;

    this.samples.push(value);
    if (this.samples.length > MAX_SAMPLES) this.samples.shift();

    this.lastActivityAt = now();

    if (value > HIGH_LATENCY_MS) {
      this.addSignal(1);
    } else {
      // Respuesta sana: se descomprime una señal (recuperación gradual).
      this.decay(1);
    }

    this.prune();
    this.emit();
  }

  /** Registra un fallo de red. Devuelve el tipo clasificado. */
  recordFailure(err: unknown): { kind: SyncErrorKind; delay: number; overload: boolean } {
    const kind = classifySyncError(err);
    const overloadError = isOverloadError(err);
    
    // Un 429/503 vale doble: es saturación confirmada, no solo lentitud.
    this.addSignal(overloadError ? 2 : 1);
    this.lastActivityAt = now();
    this.prune();
    this.emit();

    return { kind, delay: 0, overload: overloadError };
  }

  /** Marca el fin de un lote exitoso (el servidor respondió bien). */
  recordBatchSuccess(): void {
    this.lastActivityAt = now();
    this.decay(1);
    this.prune();
    this.emit();
  }

  /** Reinicia todas las señales (p. ej. al recuperar internet). */
  reset(): void {
    this.samples = [];
    this.signals = [];
    this.overload = false;
    this.lastActivityAt = 0;
    this.emit();
  }

  // ── SEÑALES ──

  private addSignal(weight: number): void {
    const at = now();
    for (let i = 0; i < weight; i++) this.signals.push(at);
    if (this.signals.length >= SOBRECARGA_SIGNALS) this.overload = true;
  }

  private decay(weight: number): void {
    for (let i = 0; i < weight; i++) this.signals.shift();
    if (this.signals.length < SOBRECARGA_SIGNALS) this.overload = false;
  }

  private prune(): void {
    const limit = now() - CONGESTION_WINDOW_MS;
    this.signals = this.signals.filter((at) => at >= limit);
    if (this.signals.length < SOBRECARGA_SIGNALS) this.overload = false;
  }

  // ── ESTADO ──

  get state(): TrafficState {
    this.prune();
    if (this.overload) return 'SOBRECARGA';
    if (this.signals.length > 0) return 'CONGESTIONADO';
    const avg = this.averageLatency;
    if (avg !== null && avg > HIGH_LATENCY_MS) return 'CONGESTIONADO';
    return 'NORMAL';
  }

  get averageLatency(): number | null {
    if (this.samples.length === 0) return null;
    return this.samples.reduce((sum, v) => sum + v, 0) / this.samples.length;
  }

  get lastLatency(): number | null {
    return this.samples.length > 0 ? this.samples[this.samples.length - 1] : null;
  }

  get signalsInWindow(): number {
    this.prune();
    return this.signals.length;
  }

  /** ms desde la última actividad de red (Infinity si nunca hubo). */
  get quietMs(): number {
    return this.lastActivityAt === 0 ? Number.POSITIVE_INFINITY : now() - this.lastActivityAt;
  }

  /** "Servidor libre": sin señales de congestión y con reposo suficiente. */
  isServerIdle(): boolean {
    return this.state === 'NORMAL' && this.signalsInWindow === 0 && this.quietMs >= IDLE_SERVER_QUIET_MS;
  }

  /** Genera un snapshot actual del estado del tráfico. */
  snapshot(): TrafficSnapshot {
    const state = this.state;
    const quiet = this.quietMs;
    
    const message =
      state === 'NORMAL'
        ? 'Servidor normal'
        : state === 'CONGESTIONADO'
          ? 'Servidor congestionado: se baja el ritmo de subida'
          : 'Servidor en sobrecarga: subida escalonada en cola';

    return {
      state,
      latencyMs: this.lastLatency,
      averageLatencyMs: this.averageLatency,
      signalsInWindow: this.signalsInWindow,
      isIdle: this.isServerIdle(),
      quietMs: Number.isFinite(quiet) ? quiet : IDLE_SERVER_QUIET_MS,
      message,
    };
  }

  // ── POLÍTICA ──

  /**
   * Decide qué hacer con un intento de sincronización.
   *
   *  · FREE: de fondo/eventos → DEFER (todo se encola). Manual → GO solo si el
   *    servidor está normal. Cierre de caja / servidor libre → GO.
   *  · PLUS: carril prioritario. En SOBRECARGA → STAGGER (Round-Robin), en
   *    CONGESTIONADO → STAGGER suave, en NORMAL → GO.
   */
  evaluate(options: EvaluateOptions): TrafficDecision {
    const { tier, trigger } = options;
    const isOnline = options.isOnline !== false;
    const state = this.state;
    const base = { state, tier, trigger } as const;

    if (!isOnline) {
      return {
        ...base,
        action: 'DEFER',
        delayMs: 0,
        reason: 'Sin conexión: el lote queda encolado y se sube al reconectar',
      };
    }

    if (tier === 'FREE') {
      switch (trigger) {
        case 'cash-close':
          return {
            ...base,
            action: 'GO',
            delayMs: 0,
            reason: 'Cierre de caja: se sube el lote consolidado',
          };
        case 'idle':
          return this.isServerIdle()
            ? {
                ...base,
                action: 'GO',
                delayMs: 0,
                reason: 'Servidor libre: ventana de subida aprovechada',
              }
            : {
                ...base,
                action: 'DEFER',
                delayMs: 0,
                reason:
                  'Servidor ocupado: la subida gratuita espera al cierre de caja o a un momento libre',
              };
        case 'manual':
          return state === 'NORMAL'
            ? {
                ...base,
                action: 'GO',
                delayMs: 0,
                reason: 'Subida manual solicitada por el usuario',
              }
            : {
                ...base,
                action: 'DEFER',
                delayMs: 0,
                reason: 'Servidor congestionado: la subida manual se reintenta en unos segundos',
              };
        default:
          // background / event: el plan gratuito NO hace subida continua.
          return {
            ...base,
            action: 'DEFER',
            delayMs: 0,
            reason:
              'Plan gratuito: la subida continua está pausada (se encola para el cierre de caja)',
          };
      }
    }

    // ── PLUS: carril prioritario exclusivo ──
    if (state === 'SOBRECARGA') {
      return {
        ...base,
        action: 'STAGGER',
        delayMs: PLUS_STAGGER_MS,
        reason: 'Carril prioritario en sobrecarga: envíos escalonados (Round-Robin)',
      };
    }
    if (state === 'CONGESTIONADO') {
      return {
        ...base,
        action: 'STAGGER',
        delayMs: PLUS_CONGESTION_STAGGER_MS,
        reason: 'Carril prioritario: ritmo suave por congestión',
      };
    }
    return {
      ...base,
      action: 'GO',
      delayMs: 0,
      reason: 'Carril prioritario: servidor normal',
    };
  }

  /**
   * Retardo Round-Robin para el envío número `index` del carril prioritario.
   * Reparte los envíos en "ranuras" para no saturar la API en bucle.
   */
  staggerDelay(
    index: number,
    decision?: Pick<TrafficDecision, 'action' | 'delayMs'>,
  ): number {
    const i = Math.max(0, Math.floor(index || 0));
    if (i === 0) return 0;

    const decisionState = decision?.action;
    const step =
      decisionState === 'STAGGER'
        ? decision?.delayMs || PLUS_STAGGER_MS
        : this.state === 'SOBRECARGA'
          ? PLUS_STAGGER_MS
          : this.state === 'CONGESTIONADO'
            ? PLUS_CONGESTION_STAGGER_MS
            : 0;

    return i * step;
  }

  // ── SUSCRIPCIÓN (para la UI) ──

  /** Suscribe un listener para recibir actualizaciones del estado. */
  subscribe(listener: (snapshot: TrafficSnapshot) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    if (this.listeners.size === 0) return;
    const snap = this.snapshot();
    this.listeners.forEach((listener) => {
      try {
        listener(snap);
      } catch {
        /* la UI nunca rompe al gobernador */
      }
    });
  }
}

/** Instancia única de la app. */
export const trafficGovernor = new TrafficGovernor();

// En desarrollo queda accesible desde la consola para PROBAR el carril
// prioritario y los estados de congestión:
//   __niizTrafficGovernor.recordLatency(4000)   → CONGESTIONADO
//   __niizTrafficGovernor.recordFailure({status:429}) → SOBRECARGA
//   __niizTrafficGovernor.reset()                → NORMAL
if (typeof window !== 'undefined') {
  const w = window as unknown as Record<string, unknown>;
  if (import.meta.env?.DEV) {
    w['__niizTrafficGovernor'] = trafficGovernor;
  }
}